"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUploader = void 0;
const tslib_1 = require("tslib");
const axios_1 = tslib_1.__importDefault(require("axios"));
const lib_storage_1 = require("@aws-sdk/lib-storage");
const client_s3_1 = require("@aws-sdk/client-s3");
const middleware_retry_1 = require("@aws-sdk/middleware-retry");
const Logger_1 = tslib_1.__importDefault(require("./Logger"));
function fileToReadableStream(file) {
    let position = 0;
    return new ReadableStream({
        pull(controller) {
            var _a;
            return tslib_1.__awaiter(this, void 0, void 0, function* () {
                const chunk = file.slice(position, Math.min(file.size, position + Math.max((_a = controller.desiredSize) !== null && _a !== void 0 ? _a : 0, 512 * 1024)));
                const buffer = yield new Response(chunk).arrayBuffer();
                const uint8array = new Uint8Array(buffer);
                const bytesRead = uint8array.byteLength;
                position += bytesRead;
                controller.enqueue(uint8array);
                if (position >= file.size)
                    controller.close();
            });
        },
    });
}
class UploadSpeedLogger {
    constructor() {
        this.startTime = 0;
        this.loaded = {};
    }
    static isLoggerEnabled() {
        return !!sessionStorage.getItem("FileUploader.enableLog");
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    log(message, ...optionalParams) {
        if (!UploadSpeedLogger.isLoggerEnabled()) {
            return;
        }
        this.log(message, ...optionalParams);
    }
    getCurrentStat() {
        const endTime = new Date().getTime();
        const duration = (endTime - this.startTime) / 1000;
        let totalBytes = 0;
        Object.values(this.loaded).forEach(bytes => (totalBytes = totalBytes + bytes));
        const speedKBps = (totalBytes / duration / 1024).toFixed(2);
        this.log("speed KBps:", speedKBps);
        return speedKBps;
    }
    startUpload() {
        if (Object.keys(this.loaded).length > 0) {
            return;
        }
        this.startTime = new Date().getTime();
    }
    stopUpload() {
        this.log("stopUpload, speed KBps:", this.getCurrentStat());
        this.startTime = 0;
        this.loaded = {};
    }
    logBytes(key, bytes) {
        this.loaded[key] = bytes;
        this.getCurrentStat();
    }
}
const logger = new UploadSpeedLogger();
class FileUploader {
    constructor(url, options) {
        this.logger = new Logger_1.default();
        this.uploadingCount = 0;
        this.maxParallelUploads = 2;
        this.cancelTokensMap = new Map();
        this.parallelUploads3 = new Map();
        this.url = url;
        this.chunkUpload = (options === null || options === void 0 ? void 0 : options.chunkUpload) ? options === null || options === void 0 ? void 0 : options.chunkUpload : false;
        this.partSize = (options === null || options === void 0 ? void 0 : options.partSize) && (options === null || options === void 0 ? void 0 : options.partSize) > 5242880 ? options === null || options === void 0 ? void 0 : options.partSize : 5242880;
        this.fileQueue = new Set();
        this.maxParallelUploads = this.chunkUpload ? 1 : this.maxParallelUploads;
    }
    upload(fileList) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            for (const uploadData of fileList) {
                yield this.uploadOneFile({
                    id: uploadData.key,
                    file: uploadData.file,
                    urlPart: uploadData.urlPart,
                    onSuccess: uploadData.cb,
                    fileType: uploadData.file.type,
                });
            }
        });
    }
    addToQueue(files) {
        files.forEach(i => this.fileQueue.add(i));
        this.uploadFromQueue();
    }
    removeFromQueue(id) {
        const upload = this.parallelUploads3.get(id);
        if (upload) {
            upload.abort();
            this.parallelUploads3.delete(id);
            this.logger.log(`[FILE-UPLOADER] remove from S3 parallel upload, fileId: ${id}`);
        }
        const cancelToken = this.cancelTokensMap.get(id);
        if (cancelToken) {
            cancelToken.cancel(`Upload for file ${id} canceled by user`);
            this.cancelTokensMap.delete(id);
            this.logger.log(`[FILE-UPLOADER] cansel a token, fileId: ${id}`);
        }
        for (const v of this.fileQueue.values()) {
            if (v.id !== id)
                continue;
            this.fileQueue.delete(v);
            this.logger.log(`[FILE-UPLOADER] delete a file from the queue, fileId: ${id}`);
            break;
        }
    }
    uploadFromQueue() {
        if (this.uploadingCount >= this.maxParallelUploads)
            return;
        const fileDataIterator = this.fileQueue.values().next();
        if (fileDataIterator.done) {
            return;
        }
        const fileData = fileDataIterator.value;
        this.fileQueue.delete(fileData);
        this.uploadingCount++;
        this.uploadOneFile(fileData).finally(() => {
            this.uploadingCount--;
            if (this.fileQueue.size === 0 && this.uploadingCount === 0) {
                logger.stopUpload();
            }
            this.uploadFromQueue();
        });
        this.uploadFromQueue();
    }
    uploadOneFile(fileData) {
        logger.startUpload();
        if (this.chunkUpload) {
            return this.uploadOneFileByChunks(fileData);
        }
        else {
            return this.uploadOneFileByPut(fileData);
        }
    }
    uploadOneFileByPut(fileData) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const loggerContext = { fileSize: fileData.file.size, fileType: fileData.fileType };
            this.logger.log(`[FILE-UPLOADER][BY-PUT] start uploading, fileId: ${fileData.id}`, loggerContext);
            const urlPart = fileData.urlPart.startsWith("/") ? fileData.urlPart : `/${fileData.urlPart}`;
            const signedUrl = this.url + urlPart;
            const source = axios_1.default.CancelToken.source();
            this.cancelTokensMap.set(fileData.id, source);
            const startUploading = new Date().getTime();
            const options = {
                cancelToken: source.token,
                headers: {
                    "Content-Type": fileData.fileType,
                    "X-Amz-Meta-Filename": encodeURIComponent(fileData.file.name),
                },
                onUploadProgress: (e) => {
                    this.logger.log(`[FILE-UPLOADER][BY-PUT] uploading is in processing, fileId: ${fileData.id}`, {
                        loaded: e.loaded,
                        total: e.total,
                        duration: `${new Date().getTime() - startUploading} ms`
                    });
                    logger.logBytes(fileData.id, e.loaded ? e.loaded : 0);
                    fileData.onUploadProgress && fileData.onUploadProgress(e);
                },
            };
            try {
                const result = yield axios_1.default.put(signedUrl, fileData.file, options);
                if (result.status === 200) {
                    yield fileData.onSuccess();
                }
            }
            catch (e) {
                this.logger.log(`[FILE-UPLOADER][BY-PUT] error uploading, fileId: ${fileData.id}`, { error: Object.assign({ message: e.message, stack: e.stack }, e) });
                if (!axios_1.default.isCancel(e)) {
                    if (fileData.onError)
                        fileData.onError(e);
                    else
                        throw e;
                }
            }
            finally {
                this.cancelTokensMap.delete(fileData.id);
                this.logger.log(`[FILE-UPLOADER][BY-PUT] end uploading, fileId: ${fileData.id}`, loggerContext);
            }
        });
    }
    uploadOneFileByChunks(fileData) {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const loggerContext = { fileSize: fileData.file.size, fileType: fileData.fileType };
            this.logger.log(`[FILE-UPLOADER][BY-CHUNKS] start uploading, fileId: ${fileData.id}`, loggerContext);
            const urlPart = fileData.urlPart.startsWith("/") ? fileData.urlPart : `/${fileData.urlPart}`;
            const regexp = new RegExp(/(^\/[^/]*\/[^/]*\/[^/]*)\/([^/]*)\/([^/]*)/);
            const lines = regexp.exec(urlPart);
            let signature = "";
            let buket = "";
            let key = "";
            if (lines) {
                signature = lines[1];
                buket = lines[2];
                key = lines[3];
            }
            const s3 = new client_s3_1.S3Client({
                endpoint: this.url + signature,
                forcePathStyle: true,
                credentials: {
                    accessKeyId: "1",
                    secretAccessKey: "1",
                },
                region: "us-east-1",
                retryStrategy: new middleware_retry_1.StandardRetryStrategy(() => tslib_1.__awaiter(this, void 0, void 0, function* () { return 3; }), { retryDecider: () => true }),
            });
            const params = {
                Body: fileToReadableStream(fileData.file),
                Key: key,
                Bucket: buket,
                ContentType: fileData.fileType,
                Metadata: {
                    "Filename": encodeURIComponent(fileData.file.name),
                },
            };
            try {
                const upload = new lib_storage_1.Upload({
                    client: s3,
                    queueSize: 4,
                    partSize: this.partSize,
                    leavePartsOnError: true,
                    params,
                });
                this.parallelUploads3.set(fileData.id, upload);
                const startUploading = new Date().getTime();
                upload.on("httpUploadProgress", (e) => {
                    var _a, _b;
                    const progress = Object.assign(Object.assign({}, e), { total: (_a = e.total) !== null && _a !== void 0 ? _a : fileData.file.size });
                    try {
                        this.logger.log(`[FILE-UPLOADER][BY-CHUNKS] chunk ${progress.part} is uploaded, fileId: ${progress.Key}`, {
                            bucket: progress.Bucket,
                            chunkSize: this.partSize,
                            loaded: progress.loaded,
                            total: progress.total,
                            duration: `${new Date().getTime() - startUploading} ms`
                        });
                        logger.logBytes(fileData.id, (_b = progress.loaded) !== null && _b !== void 0 ? _b : 0);
                        fileData.onUploadProgress && fileData.onUploadProgress(progress);
                    }
                    catch (e) {
                        this.logger.log(`[FILE-UPLOADER][BY-CHUNKS] chunk ${progress.part} progress callback error`, { error: Object.assign({ message: e.message, stack: e.stack }, e) });
                    }
                });
                yield upload.done();
                yield fileData.onSuccess();
                this.logger.log(`[FILE-UPLOADER][BY-CHUNKS] end uploading fileId: ${fileData.id}`, loggerContext);
            }
            catch (e) {
                this.logger.log(`[FILE-UPLOADER][BY-CHUNKS] uploading error fileId: ${fileData.id}`, { error: Object.assign({ message: e.message, stack: e.stack }, e) });
                if (fileData.onError)
                    fileData.onError(e);
                else
                    throw e;
            }
            finally {
                this.parallelUploads3.delete(fileData.id);
            }
        });
    }
}
exports.FileUploader = FileUploader;
//# sourceMappingURL=FileUploader.js.map